import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  LocalShipping as LocalShippingIcon,
  Edit as EditIcon,
  Add as AddIcon,
  RocketLaunch as RocketLaunchIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import TrackingUpdateModal from "./TrackingUpdateModal";

const TrackingActionsMenu = ({
  order,
  pushingToShipRocket,
  updatingTracking,
  handlePushToShipRocket,
  handleManualTrackingUpdate,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTrackingSave = (orderGuid, trackingData) => {
    handleManualTrackingUpdate(orderGuid, trackingData);
    setShowTrackingModal(false);
  };

  const handlePushToShipRocketClick = () => {
    handlePushToShipRocket(order.orderGuid);
    handleClose();
  };

  const handleManualUpdateClick = () => {
    setShowTrackingModal(true);
    handleClose();
  };

  const handleTrackClick = () => {
    if (order.trackingLink) {
      window.open(order.trackingLink, "_blank", "noopener,noreferrer");
    }
    handleClose();
  };

  const getTrackingActions = () => {
    const actions = [];

    // If tracking exists, show tracking-related actions
    if (order.trackingCode) {
      if (order.trackingLink) {
        actions.push({
          label: "Track Package",
          icon: <OpenInNewIcon fontSize="small" />,
          action: handleTrackClick,
          color: "info.main",
          disabled: false,
        });
      }

      actions.push({
        label: "Edit Tracking",
        icon: <EditIcon fontSize="small" />,
        action: handleManualUpdateClick,
        color: "primary.main",
        disabled: updatingTracking === order.orderGuid,
      });
    } else {
      // If no tracking, show options to add tracking
      actions.push({
        label: "Push to ShipRocket",
        icon: <RocketLaunchIcon fontSize="small" />,
        action: handlePushToShipRocketClick,
        color: "primary.main",
        disabled: pushingToShipRocket === order.orderGuid,
      });

      actions.push({
        label: "Manual Update",
        icon: <AddIcon fontSize="small" />,
        action: handleManualUpdateClick,
        color: "secondary.main",
        disabled: updatingTracking === order.orderGuid,
      });
    }

    return actions;
  };

  const isUpdating =
    pushingToShipRocket === order.orderGuid ||
    updatingTracking === order.orderGuid;
  const trackingActions = getTrackingActions();

  return (
    <>
      <Box sx={{ minWidth: 140 }}>
        {/* Actions Dropdown Button */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClick}
          disabled={isUpdating}
          startIcon={
            isUpdating ? (
              <CircularProgress size={16} />
            ) : (
              <LocalShippingIcon fontSize="small" />
            )
          }
          endIcon={!isUpdating && <MoreVertIcon fontSize="small" />}
          sx={{
            fontSize: "11px",
            padding: "4px 8px",
            minWidth: "auto",
            textTransform: "none",
            borderRadius: 1.5,
            "&:hover": {
              backgroundColor: "primary.50",
              borderColor: "primary.main",
            },
          }}
        >
          {isUpdating
            ? pushingToShipRocket === order.orderGuid
              ? "Pushing..."
              : "Updating..."
            : "Tracking"}
        </Button>

        {/* Material-UI Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "tracking-actions-button",
          }}
          PaperProps={{
            sx: {
              minWidth: 180,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              borderRadius: 2,
              mt: 1,
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {trackingActions.length > 0 ? (
            trackingActions.map((actionItem, index) => (
              <MenuItem
                key={index}
                onClick={actionItem.action}
                disabled={actionItem.disabled}
                sx={{
                  fontSize: "13px",
                  py: 1,
                  px: 2,
                  "&:hover": {
                    backgroundColor: "grey.50",
                  },
                  ...(actionItem.disabled && {
                    opacity: 0.6,
                    cursor: "not-allowed",
                  }),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ color: actionItem.color, display: "flex" }}>
                    {actionItem.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "13px",
                      color: actionItem.color,
                      fontWeight: 500,
                    }}
                  >
                    {actionItem.label}
                  </Typography>
                  {actionItem.disabled && (
                    <CircularProgress size={14} sx={{ ml: "auto" }} />
                  )}
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "13px", fontStyle: "italic" }}
              >
                No tracking actions available
              </Typography>
            </MenuItem>
          )}

          {trackingActions.length > 0 && (
            <div>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleClose}
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Cancel
                </Typography>
              </MenuItem>
            </div>
          )}
        </Menu>
      </Box>

      {/* Tracking Update Modal */}
      <TrackingUpdateModal
        show={showTrackingModal}
        onClose={() => setShowTrackingModal(false)}
        onSave={handleTrackingSave}
        orderGuid={order.orderGuid}
        currentTracking={
          order.trackingCode
            ? {
                courierName: order.courierName,
                trackingCode: order.trackingCode,
                trackingLink: order.trackingLink,
              }
            : null
        }
        isUpdating={updatingTracking === order.orderGuid}
      />
    </>
  );
};

export default TrackingActionsMenu;
